import { useMemo } from 'react';

import { DeserializedAppData, OpenableApp } from '@api';

import { useUserAgentDetect } from '@shared/common/providers/UserAgentDetectProvider';
import { setMagicStoreUrmSource } from '@shared/helpers/url';

export const isOpenableApp = (value: unknown): value is OpenableApp => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const hasAllProperties = (
    [
      'id',
      'appId',
      'name',
      'icon',
      'validationStatus',
      'isProductionReady',
      'websiteUrl',
      'isWebVersionAdaptive',
      'isWebVersionHasIframeSupport',
    ] as (keyof OpenableApp)[]
  ).every((key) => key in value);

  return hasAllProperties;
};

export const transformDeserializedAppToOpenableApp = ({
  id,
  attributes: {
    appLink: appId,
    appWebsite: websiteUrl,
    appIcon: icon,
    appName: name,
    link: webVersionUrl,
    desktopAppLink: desktopVersionUrl,
    status: validationStatus,
    googlePlayLink: androidVersionUrl,
    appStoreLink: iosVersionUrl,
    productionReady: isProductionReady,
    isAdaptive: isWebVersionAdaptive,
    iframeConfirm: isWebVersionHasIframeSupport,
  },
}: DeserializedAppData): OpenableApp => {
  return {
    id,
    appId,
    name,
    icon,
    validationStatus,
    isProductionReady,
    websiteUrl,
    isWebVersionAdaptive,
    isWebVersionHasIframeSupport,
    ...(webVersionUrl ? { webVersionUrl } : {}),
    ...(desktopVersionUrl ? { desktopVersionUrl } : {}),
    ...(androidVersionUrl ? { androidVersionUrl } : {}),
    ...(iosVersionUrl ? { iosVersionUrl } : {}),
  };
};

const useAppOpenLink = (
  app: OpenableApp,
): string | { isAvailableInSpace: boolean; url: string } => {
  const { isMobile, isAndroid, isIOS, isDesktop, isTablet } = useUserAgentDetect();

  const url = useMemo(() => {
    const {
      isProductionReady,
      websiteUrl,
      webVersionUrl,
      isWebVersionHasIframeSupport,
      isWebVersionAdaptive,
      desktopVersionUrl,
      androidVersionUrl,
      iosVersionUrl,
      appId,
    } = app;

    if (!isProductionReady) {
      return websiteUrl;
    }

    if (!!webVersionUrl && (isDesktop || ((isMobile || isTablet) && isWebVersionAdaptive))) {
      if (isWebVersionHasIframeSupport) {
        return {
          isAvailableInSpace: true,
          url: `/spaces?app=${appId}`,
        };
      } else {
        return webVersionUrl;
      }
    }

    if (isMobile || isTablet) {
      if (isAndroid && androidVersionUrl) {
        return androidVersionUrl;
      } else if (isIOS && iosVersionUrl) {
        return iosVersionUrl;
      }
    } else if (isDesktop && desktopVersionUrl) {
      return desktopVersionUrl;
    }

    return websiteUrl;
  }, [app, isMobile, isAndroid, isIOS, isDesktop, isTablet]);

  return typeof url === 'string' ? setMagicStoreUrmSource(url) : url;
};

export default useAppOpenLink;
